import { loadAudioConcatProcessor } from "./audioConcatProcessor.generated.js";
import { addLibsamplerateModule } from "./addLibsamplerateModule.js";
import { createAnalyserVolumeProvider, } from "./volumeProvider.js";
export class MediaDeviceOutput {
    context;
    analyser;
    gain;
    worklet;
    audioElement;
    static async create({ sampleRate, format, outputDeviceId, workletPaths, libsampleratePath, }) {
        let context = null;
        let audioElement = null;
        try {
            const supportsSampleRateConstraint = navigator.mediaDevices.getSupportedConstraints().sampleRate;
            context = new AudioContext(supportsSampleRateConstraint ? { sampleRate } : {});
            const analyser = context.createAnalyser();
            const gain = context.createGain();
            // Always create an audio element for device switching capability
            audioElement = new Audio();
            audioElement.src = "";
            audioElement.load();
            audioElement.autoplay = true;
            audioElement.style.display = "none";
            document.body.appendChild(audioElement);
            // Create media stream destination to route audio to the element
            const destination = context.createMediaStreamDestination();
            audioElement.srcObject = destination.stream;
            gain.connect(analyser);
            analyser.connect(destination);
            if (!supportsSampleRateConstraint || context.sampleRate !== sampleRate) {
                if (context.sampleRate !== sampleRate) {
                    console.warn(`[ConversationalAI] Sample rate ${sampleRate} not available, resampling to ${context.sampleRate}`);
                }
                await addLibsamplerateModule(context, libsampleratePath);
            }
            await loadAudioConcatProcessor(context.audioWorklet, workletPaths?.audioConcatProcessor);
            const worklet = new AudioWorkletNode(context, "audioConcatProcessor");
            worklet.port.postMessage({ type: "setFormat", format, sampleRate });
            worklet.connect(gain);
            await context.resume();
            // Set initial output device if provided
            if (outputDeviceId && audioElement.setSinkId) {
                await audioElement.setSinkId(outputDeviceId);
            }
            const newOutput = new MediaDeviceOutput(context, analyser, gain, worklet, audioElement);
            return newOutput;
        }
        catch (error) {
            // Clean up audio element from DOM
            if (audioElement?.parentNode) {
                audioElement.parentNode.removeChild(audioElement);
            }
            audioElement?.pause();
            if (context && context.state !== "closed") {
                await context.close();
            }
            throw error;
        }
    }
    volume = 1;
    interrupted = false;
    interruptTimeout = null;
    volumeProvider;
    constructor(context, analyser, gain, worklet, audioElement) {
        this.context = context;
        this.analyser = analyser;
        this.gain = gain;
        this.worklet = worklet;
        this.audioElement = audioElement;
        // Start the MessagePort to enable addEventListener to work
        // (required when using addEventListener instead of onmessage)
        this.worklet.port.start();
        this.volumeProvider = createAnalyserVolumeProvider(analyser, context.sampleRate);
    }
    getAnalyser() {
        return this.analyser;
    }
    getVolume() {
        return this.volumeProvider.getVolume();
    }
    getByteFrequencyData(buffer) {
        this.volumeProvider.getByteFrequencyData(buffer);
    }
    addListener(listener) {
        this.worklet.port.addEventListener("message", listener);
    }
    removeListener(listener) {
        this.worklet.port.removeEventListener("message", listener);
    }
    setVolume(volume) {
        this.volume = volume;
        this.gain.gain.value = volume;
    }
    playAudio(chunk) {
        this.gain.gain.cancelScheduledValues(this.context.currentTime);
        this.gain.gain.value = this.volume;
        if (this.interruptTimeout) {
            clearTimeout(this.interruptTimeout);
            this.interruptTimeout = null;
        }
        this.worklet.port.postMessage({ type: "clearInterrupted" });
        this.worklet.port.postMessage({ type: "buffer", buffer: chunk });
    }
    interrupt(resetDuration = 2000) {
        this.interrupted = true;
        // Clear any existing timeout
        if (this.interruptTimeout) {
            clearTimeout(this.interruptTimeout);
            this.interruptTimeout = null;
        }
        // Send interrupt message to worklet to flush queued buffers
        this.worklet.port.postMessage({ type: "interrupt" });
        // Fade out audio gain
        this.gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + resetDuration / 1000);
        // After fade completes, restore volume and accept new audio
        this.interruptTimeout = setTimeout(() => {
            this.interrupted = false;
            this.gain.gain.value = this.volume;
            this.worklet.port.postMessage({ type: "clearInterrupted" });
            this.interruptTimeout = null;
        }, resetDuration);
    }
    async setDevice(config) {
        if (!("setSinkId" in HTMLAudioElement.prototype)) {
            throw new Error("setSinkId is not supported in this browser");
        }
        // Extract outputDeviceId from config
        const outputDeviceId = config?.outputDeviceId;
        // Note: sampleRate and format cannot be changed on an existing output
        // (would require recreating the AudioContext).
        // These options are only used during initial MediaDeviceOutput.create()
        // If deviceId is undefined, use empty string which resets to default device
        await this.audioElement.setSinkId(outputDeviceId || "");
    }
    async close() {
        // Clear any pending interrupt timeout
        if (this.interruptTimeout) {
            clearTimeout(this.interruptTimeout);
            this.interruptTimeout = null;
        }
        // Remove audio element from DOM
        if (this.audioElement.parentNode) {
            this.audioElement.parentNode.removeChild(this.audioElement);
        }
        this.audioElement.pause();
        await this.context.close();
    }
}
//# sourceMappingURL=output.js.map